import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem } from '@mui/material';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DateRange } from 'src/store';
import { BaseReportComponentProps, DateTypeExpensesReport, MdaReportTypeField, MdaReportTypes } from '../../store/types';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface ExpensesProps extends BaseReportComponentProps {
  setExpensesSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangeExpensesParameter: ActionCreatorWithPayload<DateRange, string>;
  setExpensesExcludeExpenseTypeParameter: ActionCreatorWithPayload<boolean, string>;
  setExpensesReportDateTypeParameter: ActionCreatorWithPayload<DateTypeExpensesReport, string>;
  expandExpenses: ActionCreatorWithoutPayload<string>;
}

export const Expenses = ({
  parameters,
  setExpensesSelectedParameter,
  setExpensesExcludeExpenseTypeParameter,
  setExpensesReportDateTypeParameter,
  setDateRangeExpensesParameter,
  expandExpenses,
  createReportSortRequest,
  setGroupConsolidatedOption,
}: ExpensesProps): JSX.Element => {
  const handleClickExpensesSelect = () => {
    setExpensesSelectedParameter(!parameters.selectedReports?.expenses.selected);
    createReportSortRequest(!parameters.selectedReports?.expenses.selected, MdaReportTypes.Expenses.id, MdaReportTypes.Expenses.displayName);
  };

  const handleClickExpensesHidingOptions = () => {
    setExpensesExcludeExpenseTypeParameter(!parameters.selectedReports.expenses.excludeExpenseType);
  };

  const handleClickReportDateExpenses = (dates: DateRange) => {
    setDateRangeExpensesParameter(dates);
  };

  return (
    <Accordion onChange={expandExpenses} expanded={parameters.selectedReports?.expenses.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox color="primary" checked={parameters.selectedReports?.expenses.selected} onChangeHandler={handleClickExpensesSelect} />
          }
          label="Expenses"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            dateRange={{
              dateFrom: parameters?.selectedReports?.expenses?.fromDate,
              dateTo: parameters?.selectedReports?.expenses?.toDate,
              dateRangeType: parameters?.selectedReports?.expenses?.dateRangeType,
            }}
            setDateRange={handleClickReportDateExpenses}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="date-type-id">Date Type</InputLabel>

              <WO2Select
                label="Date Type"
                labelId="date-type-id"
                onChange={(e) => setExpensesReportDateTypeParameter(e.target.value as DateTypeExpensesReport)}
                value={parameters.selectedReports.expenses.dateType}
                defaultValue={DateTypeExpensesReport.TradeDate}
              >
                <MenuItem value={DateTypeExpensesReport.TradeDate}>Trade Date</MenuItem>
                <MenuItem value={DateTypeExpensesReport.TaxDate}>Tax Date</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md="auto">
            <FormControlLabel
              control={
                <WO2ToggleSwitch
                  checked={parameters.selectedReports.expenses.excludeExpenseType}
                  onChange={handleClickExpensesHidingOptions}
                  color="primary"
                  name="exclude-expense-type"
                />
              }
              label="Exclude Expense Type"
              sx={{ ml: 1 }}
            />
          </Grid>

          {setGroupConsolidatedOption && (
            <Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <ReportGroupingOption
                  value={parameters.selectedReports.expenses.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.Expenses}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
